import React, { MouseEventHandler, useContext } from "react";
import { Alert, Button, Card, Tab } from "react-bootstrap";
import { Accordion, Col, Row, Tabs } from "react-bootstrap";
import { HomePatientContext } from "../../contexts/HomePatientContext";
import { useAccordion } from "../../contexts/AccordionContext";
// layout
import SidebarPatient from "../sidebars/SidebarPatient";
import NavigationBar from "../navigation/NavigationBar";
import Footer from "../Footer";
// helper
import { infoCardComponent } from "../../helper/card";
// icons
import { FiDownload } from "react-icons/fi";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { BiInfoCircle } from "react-icons/bi";

interface HomePatientProps {
  title: string;
}

const HomePatient = ({ title }: HomePatientProps) => {
  const {
    state: homeState,
    // openReport,
    openSurvey,
  } = useContext(HomePatientContext);
  const { state: appState, handleAccordion } = useAccordion();

  React.useEffect(() => {
    document.title = title;
  }, [title]);

  const renderAccordion = (content: {
    title: string;
    body: React.ReactNode;
    active: AccordionEventKey;
    onClickFunction: MouseEventHandler;
    eventKey: string;
  }) => {
    return (
      <Accordion
        defaultActiveKey={content.eventKey}
        activeKey={content.active}
        className="my-2"
      >
        <Accordion.Item eventKey={content.eventKey}>
          <Accordion.Header onClick={content.onClickFunction}>
            {content.title}
          </Accordion.Header>
          <Accordion.Body tabIndex={0}>{content.body}</Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const getSurveyTitle = (type) => {
    const titles = {
      PRE: "Erstgespräch",
      INTERMEDIATE: "Zwischenbefragung",
      POST: "Therapieende",
      FOLLOW_UP_1: "Nachsorge 6 Monate",
      FOLLOW_UP_2: "Nachsorge 12 Monate",
      FOLLOW_UP_3: "Nachsorge 24 Monate",
    };
    return titles[type] || "Unbekannte Befragung";
  };

  // helper function to choose the survey button based on survey status
  const getSurveyButton = (survey: { started: boolean; finished: boolean }) => {
    if (survey.finished) {
      return <Button disabled>Befragung abgeschlossen</Button>;
    }
    if (survey.started) {
      return (
        <Button variant="outline-primary" onClick={openSurvey}>
          Befragung fortsetzen
        </Button>
      );
    }
    return <Button onClick={openSurvey}>Befragung öffnen</Button>;
  };

  const TimezoneNotice = () => (
    <Alert
      variant="light"
      className="border d-flex align-items-center py-2 px-3 text-secondary mb-3"
    >
      <BiInfoCircle className="me-2 flex-shrink-0" size={18} />
      <span className="small mb-0">
        Alle Zeit- und Datumsangaben beziehen sich auf die deutsche Zeitzone
        (MEZ/MESZ).
      </span>
    </Alert>
  );

  /**
   * Configuration object for accordionAktuelles
   * The accordion displays information about the current survey for patient to complete
   */
  const accordionAktuelles = {
    title: "Aktuelles",
    active: appState.patient?.current,
    onClickFunction: () =>
      handleAccordion({ input: "current", userType: "patient" }),
    eventKey: "0",
    body:
      homeState.cntStws.current === 0 ? (
        "Keine Befragung ausstehend. Die bisherigen Befragungen, abgelaufenen Befragungen und geplanten Befragungen werden Ihnen unter Befragungen angezeigt."
      ) : homeState.stws?.length > 0 ? (
        <>
          {homeState.stws.map((stw) =>
            stw.start <= homeState.today && stw.end >= homeState.today
              ? infoCardComponent({
                  key: stw.type,
                  title: getSurveyTitle(stw.type),
                  content: (
                    <>
                      <Card.Text>
                        Die Befragung muss im Zeitraum von{" "}
                        <strong>{stw.start_show}</strong> bis{" "}
                        <strong>{stw.end_show}</strong> ausgefüllt werden.
                      </Card.Text>
                      {homeState.currentSTW?.survey &&
                        getSurveyButton(homeState.currentSTW.survey)}{" "}
                    </>
                  ),
                })
              : null,
          )}
          <TimezoneNotice />
        </>
      ) : null,
  };

  const accordionProfile = {
    title: "Profil",
    active: appState.patient?.info,
    onClickFunction: () =>
      handleAccordion({ input: "info", userType: "patient" }),
    eventKey: "1",
    body: (
      <>
        <Row className="profile-data">
          <Col sm={4}>SysDok-ID</Col>
          <Col sm={8}>{homeState.userInfo?.userName}</Col>
        </Row>

        <Row className="profile-data">
          <Col sm={4}>Ambulanz</Col>
          <Col sm={8}>{homeState.userInfo?.clinicName}</Col>
        </Row>
      </>
    ),
  };

  const accordionSurvey = {
    title: "Befragungen",
    active: appState.patient?.survey,
    onClickFunction: () =>
      handleAccordion({ input: "survey", userType: "patient" }),
    eventKey: "2",
    body: (
      <Tabs defaultActiveKey="current" className="mb-3 tabs-custom">
        {[
          {
            eventKey: "current",
            title: "Aktuelle Befragung",
            surveyTimeType: homeState.cntStws.current,
            condition: (stw) =>
              stw.start <= homeState.today && stw.end >= homeState.today,
            message: "Es gibt momentan keine aktuelle Befragung.",
            body: (startSurvey, endSurvey) => (
              <>
                <Card.Text>
                  Die Befragung muss im Zeitraum von{" "}
                  <strong>{startSurvey}</strong> bis{" "}
                  <strong>{endSurvey}</strong> ausgefüllt werden.
                </Card.Text>
                {homeState.currentSTW?.survey &&
                  getSurveyButton(homeState.currentSTW.survey)}{" "}
              </>
            ),
          },
          {
            eventKey: "completed",
            title: "Abgelaufene Befragungen",
            surveyTimeType: homeState.cntStws.completed,
            condition: (stw) => stw.end < homeState.today,
            message: "Es gibt momentan keine abgelaufenen Befragungen.",
            body: (startSurvey, endSurvey) => (
              <Card.Text>
                Die Befragung war im Zeitraum vom <strong>{startSurvey}</strong>{" "}
                bis <strong>{endSurvey}</strong> verfügbar.
              </Card.Text>
            ),
          },
          {
            eventKey: "planned",
            title: "Geplante Befragungen",
            surveyTimeType: homeState.cntStws.planned,
            condition: (stw) => stw.start > homeState.today,
            message: "Es gibt momentan keine geplanten Befragungen.",
            body: (startSurvey, endSurvey) => (
              <Card.Text>
                Die Befragung wird im Zeitraum von{" "}
                <strong>{startSurvey}</strong> bis <strong>{endSurvey}</strong>{" "}
                aktiv sein.
              </Card.Text>
            ),
          },
        ].map((tab, index) => (
          <Tab eventKey={tab.eventKey} title={tab.title} key={index}>
            <Row xs={1} md={2} lg={3}>
              {tab.surveyTimeType !== 0 ? (
                homeState.stws?.length > 0 &&
                homeState.stws.map((stw, stwsIndex) => {
                  return tab.condition(stw) ? (
                    <React.Fragment key={stwsIndex}>
                      <div className="d-flex align-items-stretch p-1">
                        {infoCardComponent({
                          key: stwsIndex,
                          title: tab.title + ": " + getSurveyTitle(stw.type),
                          content: tab.body(stw.start_show, stw.end_show),
                        })}
                      </div>
                    </React.Fragment>
                  ) : null;
                })
              ) : (
                <div className="w-100">{tab.message}</div>
              )}
            </Row>
            <TimezoneNotice />
          </Tab>
        ))}
      </Tabs>
    ),
  };

  const accordionReport = {
    title: "Berichte",
    active: appState.patient?.report,
    onClickFunction: () =>
      handleAccordion({ input: "report", userType: "patient" }),
    eventKey: "3",
    body: (
      <>
        {homeState.reports.length > 0 ? (
          homeState.reports.map((report, index) => {
            return report !== null ? (
              <React.Fragment key={index}>
                <div className="d-flex align-items-stretch p-1">
                  {["FOLLOW_UP_1", "FOLLOW_UP_2"].includes(report.stw_type)
                    ? infoCardComponent({
                        key: "" + index,
                        title: "Bericht: " + getSurveyTitle(report.stw_type),
                        //Button is replaced with a placeholder message when patient reports are not available
                        content: (
                          /**  <>
                    <Card.Text>
                      Sie können Ihren Bericht hier einsehen.
                    </Card.Text>
                    <Button onClick={(e) => openReport(e, index)}>Bericht öffnen</Button>
                  </> */
                          <Card.Text className="text-muted fst-italic">
                            Berichte sind derzeit nicht verfügbar.
                          </Card.Text>
                        ),
                      })
                    : null}
                </div>
              </React.Fragment>
            ) : null;
          })
        ) : (
          <div className="w-100">Momentan sind keine Berichte verfügbar.</div>
        )}
      </>
    ),
  };

  const accordionClinic = {
    title: "Ambulanz",
    active: appState.patient?.clinics,
    onClickFunction: () =>
      handleAccordion({ input: "clinics", userType: "patient" }),
    eventKey: "4",
    body: (
      <>
        Ambulanz-Name: <em>{homeState.userInfo?.clinicName}</em>
        <br />
        Kontakt: <em>{homeState.userInfo?.clinicContact ?? ""}</em>
      </>
    ),
  };

  return (
    <div id="body">
      {/* navigation bar (bar on the top) */}
      <NavigationBar />
      <div className="container-fluid">
        <div className="row">
          {/* Sidebar (bar on the side) */}
          <SidebarPatient />
          {/* Content */}
          <div id="main-content" className="col container p-3 ">
            <Row>
              <Col>
                {/* Aktuelles/ current Accordion */}
                {renderAccordion(accordionAktuelles)}
              </Col>
              <Col className="col-8">
                {/* Profil/profile/ information Accordion */}
                {renderAccordion(accordionProfile)}
              </Col>
            </Row>
            {/* Befragungen/Survey Accordion */}
            {renderAccordion(accordionSurvey)}
            {/* Berichte/report Accordion */}
            {renderAccordion(accordionReport)}
            {/* Ambulanz/clinic Accordion */}
            {renderAccordion(accordionClinic)}
            <Row>
              <Col>
                {infoCardComponent({
                  key: "instructions",
                  title: "Anleitung",
                  content: (
                    <>
                      <a
                        tabIndex={-1}
                        href="https://sysdok.de/%5Efiler/canonical/1698387252/146/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="primary">
                          <FiDownload /> Anleitung herunterladen
                        </Button>
                      </a>
                    </>
                  ),
                })}
              </Col>
              {/* TODO: for now:
                          `Hilfreiche Anlaufstellen bei psychischer Belastung`
                          should not be shown for SysDok.
                          Code stays here in case it might be required later.*/}
              {/*
                <Card className="card-default card-custom" tabIndex={0}>
                  <Card.Body>
                    <Card.Title role="heading" aria-level={5}>
                      Hilfreiche Anlaufstellen bei psychischer Belastung
                    </Card.Title>
                    <Card.Text>
                      Hier finden Sie eine Sammlung an hilfreichen Anlaufstellen
                      bis hin zu Selbsthilfeangeboten.
                    </Card.Text>

                    <a
                      tabIndex={-1}
                      href={`${process.env.PUBLIC_URL}/files/Hilfreiche_Anlaufstellen.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="primary">
                        <FiDownload /> Liste herunterladen
                      </Button>
                    </a>
                  </Card.Body>
                </Card>
*/}
            </Row>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default HomePatient;
